'use client';

import * as React from 'react';
import { cn } from '../lib/utils';

export interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Input pour code OTP — N cases (par défaut 6) avec navigation au clavier,
 * collage et remplissage automatique (`one-time-code`, code entier reçu dans
 * une case). Ne traite que des chiffres ; `onComplete` dès les N chiffres.
 */
export function OtpInput({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled,
  autoFocus,
  className,
}: OtpInputProps) {
  const inputsRef = React.useRef<Array<HTMLInputElement | null>>([]);

  const setRef = React.useCallback(
    (index: number) => (el: HTMLInputElement | null) => {
      inputsRef.current[index] = el;
    },
    [],
  );

  React.useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus();
  }, [autoFocus]);

  const complete = (code: string) => {
    if (onComplete && code.length === length && /^\d+$/.test(code)) onComplete(code);
  };

  const handleChange = (index: number, raw: string) => {
    if (disabled) return;
    const digits = raw.replace(/\D/g, '');
    const prev = value[index] ?? '';
    // Plusieurs chiffres d'un coup dans une case : remplissage automatique du code reçu par email
    // (iOS « Depuis Mail », Android, gestionnaires de mots de passe) ou collage sans événement paste.
    // Le code entier est réparti dans les cases — avant, `maxLength={1}` + « dernier chiffre » n'en gardait
    // qu'un, et l'élève devait retourner dans sa boîte mail.
    const overwrite = digits.length === 2 && prev !== '' && digits.includes(prev);
    if (digits.length > 1 && !overwrite) {
      const code =
        digits.length >= length
          ? digits.slice(0, length)
          : (value.slice(0, index) + digits).slice(0, length);
      onChange(code);
      inputsRef.current[Math.min(code.length, length - 1)]?.focus();
      complete(code);
      return;
    }
    // Un chiffre tapé (ou tapé par-dessus celui de la case : on garde le nouveau)
    const digit = overwrite ? (digits[0] === prev ? digits[1]! : digits[0]!) : digits;
    const next = value.split('');
    next[index] = digit;
    const joined = next.join('').slice(0, length);
    onChange(joined);

    if (digit && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
    complete(joined);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
      const next = value.split('');
      next[index] = '';
      onChange(next.join(''));
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    complete(pasted);
    inputsRef.current[Math.min(pasted.length, length - 1)]?.focus();
  };

  return (
    <div className={cn('flex w-full justify-center gap-1.5 sm:gap-3', className)}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={setRef(index)}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          value={value[index] ?? ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-label={`Chiffre ${index + 1}`}
          className={cn(
            'h-12 min-w-0 max-w-[3.5rem] flex-1 text-center text-xl font-semibold sm:h-14 sm:text-2xl',
            'border-ink-200 text-ink-900 rounded-xl border-2 bg-white',
            'transition-all duration-150',
            'focus:border-primary-500 focus:ring-primary-500/15 focus:outline-none focus:ring-4',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />
      ))}
    </div>
  );
}
